interface CreateCacheModifierProps {
  cache: any;
  createdDoc: any;
  fragment: any,
  fragmentName: string;
  modelName: string;
  append?: boolean;
}

export const createCacheModifier = ({ cache, createdDoc, fragment, modelName, fragmentName, append = false }: CreateCacheModifierProps) =>
  (existingRefs = [], { readField }: any) => {
    const createdDocs = Array.isArray(createdDoc) ? createdDoc : [createdDoc];
    const nextResult: any[] = [...existingRefs];

    createdDocs.forEach(currDoc => {
      // Without an id the write lands on `Model:undefined` and the list it joins reads back
      // incomplete, so the whole query it belongs to stops resolving.
      if (!currDoc?._id) {
        console.warn(`createCacheModifier: skipping a ${modelName} with no _id; nothing written to the cache`);

        return;
      }

      const createdRef = cache.writeFragment({
        id: `${modelName}:${currDoc._id}`,
        data: currDoc,
        fragment,
        fragmentName
      });

      if (!existingRefs.some(ref => readField('_id', ref) === currDoc._id)) {
        if (append) {
          nextResult.push(createdRef);
        } else {
          nextResult.unshift(createdRef);
        }
      }
    });

    return nextResult;
  };

// Whether any cached copy of a root list field holds the document; reads only, the list is returned unchanged.
export const isInCachedList = (cache: any, fieldName: string, _id: string) => {
  let found = false;
  cache.modify({
    fields: {
      [fieldName]: (existingRefs: any[] = [], { readField }: any) => {
        found = found || existingRefs.some(ref => readField('_id', ref) === _id);
        return existingRefs;
      },
    },
  });

  return found;
};

export const removeCacheModifier = (cache: any, _id: string) =>
  (existingRefs = [], { readField }: any) => {
    return existingRefs.filter(ref => _id !== readField('_id', ref));
  };

export const removeCacheModifiers = (cache: any, ids: string[]) =>
  (existingRefs = [], { readField }: any) => {
    return existingRefs.filter(ref => !ids.includes(readField('_id', ref)));
  };

// Moves an already-cached entity into another list field. Use this instead of
// createCacheModifier when the mutation payload isn't the entity itself.
export const addRefCacheModifier = (modelName: string, _id: string, append = false) =>
  (existingRefs = [], { readField, toReference }: any) => {
    if (existingRefs.some(ref => readField('_id', ref) === _id)) return existingRefs;

    const existingRef = toReference(`${modelName}:${_id}`);

    return append ? [...existingRefs, existingRef] : [existingRef, ...existingRefs];
  };
